import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { PageHeader as EdsPageHeader } from '~/eds/src/modules/PageHeader/PageHeader';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { AskAnythingButton } from '~/features/ask-anything/ask-anything-button/AskAnythingButton';
import { useCandu } from '~/features/onboarding/Candu/useCandu';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useDashboardsSubnavs } from '~/hooks/useDashboardsSubnavs';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { usePermission } from '~/hooks/usePermission';
import { useSearchModal } from '~/hooks/useSearchModal';
import { slices } from '~/redux/slices';
import { selectPageBanner } from '~/redux/slices/pages';
import search from '~/redux/slices/search';
import { RootState } from '~/redux/types';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { logout } from '~/utils/logout';
import { createNavlinks } from '~/utils/navLinks';

import { trackSegment } from '../SegmentAnalytics';
import { useGetWorkdayUpcApps } from './useGetWorkdayUpcApps';

export const PageHeader = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useCurrentUser();
  const { location, navigate } = useRouting();

  const [searchModal, setModal] = useSearchModal();

  const { pathname } = location;

  // initialize candu once the page is loaded (consider moving it further upstream, but after the user has logged in)
  useCandu(user);

  const banner = useSelector((state: RootState) =>
    selectPageBanner(state, pathname),
  );

  const { dashboards } = useDashboardsSubnavs();
  const hasClauseLibrary = useFlag(FlagType.ClauseLibrary);
  const enableFieldAi = useFlag(FlagType.FieldAi);
  const hasAskAnything = useFlag(FlagType.AskAnything);
  const hasDeprecateAnalyzer = useFlag(FlagType.DeprecateAnalyzer);
  const hasPlaybooks = useFlag(FlagType.Playbooks);
  const hasGroupedTicketsAndDocs = useFlag(FlagType.GroupedTicketsAndDocs);
  const hasWorkflowRoutingAgent = useFlag(FlagType.WorkflowRoutingAgent);
  // For details on CLM Evolution Phase 1 project, see Jira epic: https://jira2.workday.com/browse/EKP-40675
  const hasClmEvolutionPhase1 = useFlag(FlagType.CLMEvolutionPhase1);

  const enableCandu = useFlag(FlagType.CanduScript);

  const dashboard = useSelector(
    slices.dashboardV2.selectors.selectActiveDashboard,
  );
  const activeSearch = useSelector(slices.searchV3.selectors.selectSavedSearch);
  const dashboardAndPathname = { ...dashboard, pathname };

  const onNavLinkClick = (pathname: string) => {
    // TODO: the relevant domain pages should own the state cleanup logic
    dispatch(search.actions.reset());
    dispatch(slices.dashboardV2.actions.reset());
    dispatch(slices.searchV3.actions.reset());
    navigate(pathname);
  };

  const hasSettings = useHasFeatureFlag(FeatureFlagType.Settings);

  const hasDocumentsAccess = usePermission({
    permission: { resourceId: 'documents', resourceType: 'view' },
  });

  const hasDocumentGroupsAccess = usePermission({
    permission: { resourceId: 'document_groups', resourceType: 'view' },
  });

  const hasWorkflowTicketsAccess = usePermission({
    permission: { resourceId: 'workflow_tickets', resourceType: 'view' },
  });

  const hasWorkflowBuilderAccess = usePermission({
    permission: { resourceId: 'workflow_templates', resourceType: 'view' },
  });

  const hasClauseLibraryAccess = usePermission({
    permission: { resourceId: 'clause_library', resourceType: 'view' },
  });

  const hasClauseSamples = usePermission({
    permission: { resourceId: 'clause_samples', resourceType: 'view' },
  });

  const hasDocumentXRay = usePermission({
    permission: { resourceId: 'document_x_ray', resourceType: 'view' },
  });

  const hasDocumentsDashboard = usePermission({
    permission: { resourceId: 'documents_dashboard', resourceType: 'view' },
  });

  const hasWorkflowsDashboard = usePermission({
    permission: { resourceId: 'workflows_dashboard', resourceType: 'view' },
  });

  const { hasPermission: hasEntityGroupsAccess } = usePermission({
    permission: { resourceId: 'entity_groups', resourceType: 'view' },
  });

  // TODO: add RBAC permissions
  const hasManagedEntityParties = true;

  const hasAskAnythingPermission = usePermission({
    permission: {
      resourceId: 'conversational_ai',
      resourceType: 'edit',
    },
  });

  const links = useMemo(
    () =>
      createNavlinks(
        user,
        intl,
        onNavLinkClick,
        dashboards,
        activeSearch,
        hasClauseLibrary,
        hasDeprecateAnalyzer,
        hasPlaybooks,
        hasWorkflowRoutingAgent,
        dashboardAndPathname,
        hasGroupedTicketsAndDocs,
        hasClmEvolutionPhase1,
        {
          documents: !!hasDocumentsAccess.hasPermission,
          documentGroups: !!hasDocumentGroupsAccess.hasPermission,
          entityGroups: hasGroupedTicketsAndDocs && !!hasEntityGroupsAccess,
          workflowTickets: !!hasWorkflowTicketsAccess.hasPermission,
          workflowBuilder: !!hasWorkflowBuilderAccess.hasPermission,
          clauseLibrary: !!hasClauseLibraryAccess.hasPermission,
          automation: !!hasClauseSamples.hasPermission,
          documentXRay: enableFieldAi && !!hasDocumentXRay.hasPermission, // IMPORTANT to gate behind feature flag as we continue to refactor formally for RBAC permissions
          documentsDashboard: !!hasDocumentsDashboard.hasPermission,
          workflowsDashboard: !!hasWorkflowsDashboard.hasPermission,
          managedEntityParties: !!hasManagedEntityParties,
        },
      ),

    [
      user,
      dashboards,
      activeSearch,
      hasDocumentsAccess,
      hasDocumentGroupsAccess,
      hasWorkflowTicketsAccess,
      hasWorkflowBuilderAccess,
      hasClauseLibraryAccess,
      hasClauseSamples,
      hasDocumentsDashboard,
      hasWorkflowsDashboard,
      hasGroupedTicketsAndDocs,
      hasWorkflowRoutingAgent,
      hasClmEvolutionPhase1,
    ],
  );

  const help = {
    actions: [
      {
        label: intl.formatMessage({
          id: 'PageHeader.knowledgeCenter.',
          defaultMessage: 'Knowledge Center',
          description: 'Label for the knowledge center menu action',
        }),
        href:
          getEnvVariable('ZENDESK_AUTH_ENTRY_URL') ||
          'https://support.evisort.com/hc/en-us',
        value: 'knowledge-center',
      },

      {
        label: intl.formatMessage({
          id: 'PageHeader.evisortAcademy',
          defaultMessage: 'Evisort Academy',
          description: 'Label for the Evisort Academy menu action',
        }),
        href: 'https://academy.evisort.com/',
        value: 'evisort-academy',
      },
    ],
  };

  const profile = useMemo(() => {
    const settings = [];
    if (hasSettings) {
      settings.push({
        label: intl.formatMessage({
          id: 'PageHeader.profileSettings',
          defaultMessage: 'Settings',
          description: 'Label for the Settings menu action',
        }),
        pathname: '/settings',
        value: 'profileSettings',
      });
    }

    if (hasDocumentsAccess.hasPermission) {
      settings.push({
        label: intl.formatMessage({
          id: 'PageHeader.deletedItems',
          defaultMessage: 'My Deleted Items',
          description: 'Label for the My Deleted Items menu action',
        }),
        pathname: '/my-deleted-items',
        value: 'myDeletedItems',
      });
    }

    settings.push({
      label: intl.formatMessage({
        id: 'PageHeader.logOut',
        defaultMessage: 'Log out',
        description: 'Label for the Log out menu action',
      }),
      onClick: () => logout(user),
      value: 'logOut',
    });

    return { settings, user };
  }, [user, hasDocumentsAccess]);

  const onSearchClick = () => {
    trackSegment('selectNavigation', {
      name: 'Quick Search',
    });
    setModal();
  };

  const { apps, isWorkdayUser } = useGetWorkdayUpcApps();

  // TODO: this is not ideal and should be driven via `routes.ts` configuration to include/hide the header.  But this logic needs significant incremental refactoring to decouple this module.
  if (
    [
      RoutePathType.Login,
      RoutePathType.LoginEmbed,
      RoutePathType.LoginEmbedSuccess,
    ].includes(location.pathname as RoutePathType)
  ) {
    return null;
  }

  return (
    <>
      <EdsPageHeader
        apps={isWorkdayUser ? apps : []}
        ctaButton={
          hasAskAnything && hasAskAnythingPermission.hasPermission ? (
            <AskAnythingButton />
          ) : undefined
        }
        isLoading={
          hasDocumentsAccess.isLoading ||
          hasDocumentGroupsAccess.isLoading ||
          hasWorkflowTicketsAccess.isLoading ||
          hasWorkflowBuilderAccess.isLoading ||
          hasClauseLibraryAccess.isLoading ||
          hasClauseSamples.isLoading ||
          hasAskAnythingPermission.isLoading
        }
        activePathname={pathname}
        banner={banner}
        help={enableCandu ? { actions: [] } : help}
        links={links}
        profile={profile}
        theme={isWorkdayUser ? 'light' : 'dark'}
        onSearchClick={onSearchClick}
      />

      {searchModal}
    </>
  );
};
